// VersementCard.tsx

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {useHidePostActions} from "@/auth/hooks/useHidePostActions";
import useImageCompressor from "@/hooks/useImageCompressor";
import { useMutateData } from "@/hooks/useMutation";
import usePostData from "@/hooks/usePostData";
import usePostMultipart from "@/hooks/usePostMultipart";
import { convertEuroToCurrency, convertToEuro } from "@/libs/amount";
import {
  fetchHexaRateRateToEuroAtDate,
  readRealFundingContractDateRaw,
} from "../new_fundings/realFundingHistoricalRate";
import {
  AlertTriangle,
  ArrowRightLeft,
  Check,
  ChevronRight,
  Info,
  Pencil,
  Plus,
  Trash,
  Upload,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

type CurrencyObj = { code: string; symbol?: string; [k: string]: any };
type VersementMoveTarget = { id: number; label: string };

const toPositiveNumberOrNull = (value: any): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const normalizeDateOnly = (value: any): string | null => {
  if (!value) return null;
  if (typeof value === "string") {
    const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (match?.[1]) return match[1];
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

const VersementCard = ({
  currency, // objet devise non-EUR
  currencySelected, // string | null -> code devise sélectionnée; null => EUR
  desiredFundings, // laissé tel quel même si non utilisé, pour compat compat
  funding, // doit contenir .id et .realFundingExpectedPaids
  onChange,
  onApiList,
  readonly = false,
  allowMove = false,
  moveTargets = [],
}: {
  currency: CurrencyObj | null;
  currencySelected: string | null;
  desiredFundings?: any;
  funding: any;
  onChange: (rows: any[]) => void;
  onApiList?: (apiRes: any) => void;
  readonly?: boolean;
  allowMove?: boolean;
  moveTargets?: VersementMoveTarget[];
}) => {
  const isVerdeoFunding = !!funding?.verdeo;
  const fundingContractDate = useMemo(
    () => normalizeDateOnly(readRealFundingContractDateRaw(funding)),
    [funding],
  );
  const fundingCurrencyCode = useMemo(
    () =>
      String(
        currency?.code ??
          funding?.project?.country?.currency?.code ??
          funding?.paidDevise?.code ??
          funding?.paid_devise?.code ??
          "",
      )
        .trim()
        .toUpperCase(),
    [
      currency?.code,
      funding?.project?.country?.currency?.code,
      funding?.paidDevise?.code,
      funding?.paid_devise?.code,
    ],
  );

  const [contractRateToEuroFromApi, setContractRateToEuroFromApi] = useState<
    number | null
  >(null);
  useEffect(() => {
    let cancelled = false;
    setContractRateToEuroFromApi(null);

    (async () => {
      if (
        isVerdeoFunding ||
        !fundingContractDate ||
        !fundingCurrencyCode ||
        fundingCurrencyCode === "EUR"
      ) {
        if (!cancelled) {
          setContractRateToEuroFromApi(
            fundingCurrencyCode === "EUR" ? 1 : null,
          );
        }
        return;
      }

      const rate = await fetchHexaRateRateToEuroAtDate(
        fundingCurrencyCode,
        fundingContractDate,
      );
      if (!cancelled) {
        setContractRateToEuroFromApi(
          typeof rate === "number" && rate > 0 ? rate : null,
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isVerdeoFunding, fundingContractDate, fundingCurrencyCode]);

  const fundingStoredOldRateToEuro = useMemo(
    () => toPositiveNumberOrNull(funding?.oldRate ?? funding?.old_rate),
    [funding?.oldRate, funding?.old_rate],
  );
  const fundingDerivedRateToEuro = useMemo(() => {
    const paidAmountEur = Number(
      funding?.paidAmountEUR ?? funding?.paidAmount ?? funding?.paid_amount,
    );
    const paidAmountConverted = Number(
      funding?.paidAmountConverted ??
        funding?.paid_amountConverted ??
        funding?.paid_amount_converted,
    );
    if (
      Number.isFinite(paidAmountEur) &&
      paidAmountEur > 0 &&
      Number.isFinite(paidAmountConverted) &&
      paidAmountConverted > 0
    ) {
      const ratio = paidAmountEur / paidAmountConverted;
      return Number.isFinite(ratio) && ratio > 0 ? ratio : null;
    }
    return null;
  }, [
    funding?.paidAmountEUR,
    funding?.paidAmount,
    funding?.paid_amount,
    funding?.paidAmountConverted,
    funding?.paid_amountConverted,
    funding?.paid_amount_converted,
  ]);

  const fundingRateToEuro = useMemo(() => {
    if (isVerdeoFunding) {
      return fundingStoredOldRateToEuro ?? fundingDerivedRateToEuro;
    }
    return contractRateToEuroFromApi ?? fundingDerivedRateToEuro ?? null;
  }, [
    isVerdeoFunding,
    contractRateToEuroFromApi,
    fundingStoredOldRateToEuro,
    fundingDerivedRateToEuro,
  ]);

  const fundingRateDate = useMemo(
    () =>
      !isVerdeoFunding
        ? fundingContractDate
        : normalizeDateOnly(
            fundingContractDate ??
              funding?.dateOldRate ??
              funding?.date_old_rate ??
              funding?.dateAmount ??
              funding?.date_amount ??
              null,
          ),
    [
      isVerdeoFunding,
      fundingContractDate,
      funding?.dateOldRate,
      funding?.date_old_rate,
      funding?.dateAmount,
      funding?.date_amount,
    ],
  );

  const resolveRateToEuro = (row: any): number | null => {
    if (!isVerdeoFunding) return fundingRateToEuro;
    const rowRate = toPositiveNumberOrNull(row?.oldRate ?? row?.old_rate);
    return rowRate ?? fundingRateToEuro;
  };

  const resolveRateDate = (row: any): string | null =>
    !isVerdeoFunding
      ? fundingRateDate
      : normalizeDateOnly(row?.dateOldRate ?? row?.date_old_rate ?? null) ??
        fundingRateDate;

  const convertEuroAmountToDisplayed = async (
    amountEur: number,
    row: any,
  ): Promise<number> => {
    if (!(isFXSelected && currency)) return amountEur;

    const storedConverted = Number(
      row?.amountConverted ?? row?.amount_converted,
    );
    if (Number.isFinite(storedConverted) && storedConverted > 0) {
      return storedConverted;
    }

    const rateToEuro = resolveRateToEuro(row);
    if (rateToEuro) {
      return amountEur / rateToEuro;
    }
    if (!isVerdeoFunding) {
      return amountEur;
    }
    return await convertEuroToCurrency(amountEur, currency);
  };

  const convertDisplayedAmountToEuro = async (
    amountDisplayed: number,
    row: any,
  ): Promise<number> => {
    if (!(isFXSelected && currency)) return amountDisplayed;
    const rateToEuro = resolveRateToEuro(row);
    if (rateToEuro) {
      return amountDisplayed * rateToEuro;
    }
    if (!isVerdeoFunding) {
      return NaN;
    }
    return await convertToEuro(amountDisplayed, currency);
  };

  /** ================== Helpers ================== */
  const normalizeIncoming = (v: any) => {
    const baseAmountEur = Number(v.amount ?? 0);
    const rowRate = resolveRateToEuro(v);
    const rawAmountConverted = Number(v.amountConverted ?? v.amount_converted);
    const amountConverted =
      Number.isFinite(rawAmountConverted) && rawAmountConverted >= 0
        ? rawAmountConverted
        : rowRate
          ? baseAmountEur / rowRate
          : null;

    return {
      ...v,
      expectedAt: v.at ? String(v.at).slice(0, 10) : v.expectedAt ?? null,
      at: v.datePaid
        ? String(v.datePaid).slice(0, 10)
        : v.at
        ? String(v.at).slice(0, 10)
        : "",
      status: v.status === 0 ? 1 : v.status ?? 1,
      paymentSlip: v.paymentSlip || { id: null, name: "", uri: "#" },
      amountConverted,
      oldRate: rowRate,
      dateOldRate: resolveRateDate(v),
      amount: baseAmountEur, // base en EUR côté back
    };
  };

  /** ================== State ================== */
  // Normalisation initiale
  const [localVersements, setLocalVersements] = useState(
    (funding?.realFundingExpectedPaids || []).map(normalizeIncoming)
  );

  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [originalVersement, setOriginalVersement] = useState<any>(null);
  const [openDetailsIndex, setOpenDetailsIndex] = useState<number | null>(null); // panneau (date virement + PJ)
  const [savingVersementId, setSavingVersementId] = useState<string | number | null>(null);
  const savingVersementIdRef = useRef<string | number | null>(null);
  const [expectedOverflowPopoverOpen, setExpectedOverflowPopoverOpen] =
    useState(false);
  const [moveIndex, setMoveIndex] = useState<number | null>(null);
  const [moveTargetRealFundingId, setMoveTargetRealFundingId] = useState<
    string
  >("");
  const [movingPaymentId, setMovingPaymentId] = useState<string | number | null>(
    null,
  );

  const { postData } = usePostData();
  const hidePostActions = useHidePostActions();

  const { mutate: saveData } = useMutateData({
    method: "POST",
    id: "verdeo-fundings-project",
    onSuccess: () => toast("Les données ont été bien mises à jour."),
  });

  const { postMultipart } = usePostMultipart();
  const { handleCompressImage } = useImageCompressor();

  // Helpers devise sélectionnée
  const isFXSelected = currencySelected === (currency?.code || "");
  const currencySymbol = isFXSelected
    ? currency?.symbol || currency?.code || ""
    : "€";
  const contractAmountEur = useMemo(() => {
    const amount = Number(
      funding?.paidAmountEUR ?? funding?.paidAmount ?? funding?.paid_amount,
    );
    return Number.isFinite(amount) && amount > 0 ? amount : 0;
  }, [funding?.paidAmountEUR, funding?.paidAmount, funding?.paid_amount]);
  const totalExpectedPaymentsEur = useMemo(
    () =>
      localVersements.reduce((sum, versement) => {
        const amount = Number(versement?.amount ?? 0);
        if (!Number.isFinite(amount) || amount <= 0) return sum;

        const rowRate = resolveRateToEuro(versement);
        if (isFXSelected && rowRate) {
          return sum + amount * rowRate;
        }

        if (isFXSelected && !isVerdeoFunding) {
          return sum + amount;
        }

        return sum + amount;
      }, 0),
    [isFXSelected, isVerdeoFunding, localVersements],
  );
  const expectedPaymentsOverflowEur = useMemo(() => {
    const overflow = totalExpectedPaymentsEur - contractAmountEur;
    return Number.isFinite(overflow) && overflow > 0 ? overflow : 0;
  }, [totalExpectedPaymentsEur, contractAmountEur]);
  const expectedPaymentsExceedContract =
    contractAmountEur > 0 && expectedPaymentsOverflowEur > 0.0001;
  const expectedPaymentsExceedMessage = useMemo(
    () =>
      `Le total des versements prévus (${Number(
        totalExpectedPaymentsEur,
      ).toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} €) dépasse le montant du contrat (${Number(
        contractAmountEur,
      ).toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} €) de ${Number(expectedPaymentsOverflowEur).toLocaleString(
        "fr-FR",
        {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        },
      )} €.`,
    [totalExpectedPaymentsEur, contractAmountEur, expectedPaymentsOverflowEur],
  );

  /** ================== Nouvelle sync après mise à jour des props ==================
   * Quand `funding.realFundingExpectedPaids` change (ex: après sauvegarde via le wizard),
   * on ré-injecte la liste dans l'état local pour que l'affichage se mette à jour
   * immédiatement (sans rechargement de page).
   * On applique la conversion visuelle si une devise non-EUR est sélectionnée.
   */
  useEffect(() => {
    let cancelled = false;
    const syncFromFunding = async () => {
      const raw = (funding?.realFundingExpectedPaids || []).map(
        normalizeIncoming
      );

      // Conversion visuelle si FX sélectionnée
      const next =
        isFXSelected && currency
          ? await Promise.all(
              raw.map(async (v) => ({
                ...v,
                amount: await convertEuroAmountToDisplayed(
                  Number(v.amount ?? 0),
                  v,
                ),
              }))
            )
          : raw;

      if (!cancelled) {
        setLocalVersements(next);
        setEditIndex(null);
        setOriginalVersement(null);
        setOpenDetailsIndex(null);
        setMoveIndex(null);
        setMoveTargetRealFundingId("");
        setMovingPaymentId(null);
        onChange(next);
      }
    };

    syncFromFunding();
    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    funding?.id,
    currency?.code,
    isFXSelected,
    fundingRateToEuro,
    fundingRateDate,
    JSON.stringify(funding?.realFundingExpectedPaids || []),
  ]);

  /** ================== TRI D’AFFICHAGE PAR expectedAt (ASC) ================== */
  const displayVersements = useMemo(() => {
    const ts = (d?: string | null) => {
      if (!d) return Number.POSITIVE_INFINITY; // sans date prévue -> fin
      const t = Date.parse(d);
      return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
    };
    return [...localVersements].sort((a, b) => {
      const da = ts(a.expectedAt);
      const db = ts(b.expectedAt);
      if (da !== db) return da - db;
      return String(a.id).localeCompare(String(b.id));
    });
  }, [localVersements]);

  /** ================== Upload PJ (compress si image, puis envoie) ================== */
  const uploadMedia = async (baseIndex: number, file: File) => {
    if (readonly) return;
    const data = new FormData();
    data.append("type", "12");

    const fileDefaultName = file.name;
    let compressedBlob: Blob | null = null;
    try {
      compressedBlob = await handleCompressImage(file, 1024, 768, 0.9);
    } catch {
      compressedBlob = null;
    }

    if (compressedBlob) {
      const renamedFile = new File([compressedBlob], fileDefaultName, {
        type: compressedBlob.type,
      });
      data.append("files[]", renamedFile);
    } else {
      data.append("files[]", file);
    }

    const newMedias: any = await postMultipart({
      url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
      data,
    });

    if (!Array.isArray(newMedias)) {
      toast(newMedias?.message || "Erreur lors de l'upload");
      return;
    }

    const updated = [...localVersements];
    updated[baseIndex].paymentSlip = newMedias[0];
    setLocalVersements(updated);
  };

  /** ================== Handlers locaux ================== */
  const handleChange = (baseIndex: number, key: string, value: any) => {
    if (readonly) return;
    const updated = [...localVersements];
    switch (key) {
      case "amount":
        updated[baseIndex].amount =
          typeof value === "number"
            ? value
            : parseFloat(String(value).replace(",", ".")) || 0;
        break;
      case "expectedAt":
        updated[baseIndex].expectedAt = String(value ?? "");
        break;
      case "status": {
        const prevReceived = updated[baseIndex].status === 2;
        let v = Number(value);
        if (v === 0) v = 1; // 0 -> 1 (sécurité legacy)
        updated[baseIndex].status = v;
        const nowReceived = v === 2;
        if (!prevReceived && nowReceived) setOpenDetailsIndex(baseIndex);
        else if (prevReceived && !nowReceived) setOpenDetailsIndex(null);
        break;
      }
      case "at":
        updated[baseIndex].at = String(value ?? "");
        break;
      default:
        updated[baseIndex][key] = value;
    }
    setLocalVersements(updated);
  };

  /** ================== SAVE ================== */
  const handleSave = async (baseIndex: number) => {
    if (readonly) return;
    const updated = [...localVersements];
    const v = updated[baseIndex];
    const rowId = v?.id ?? baseIndex;

    if (savingVersementIdRef.current === rowId) {
      return;
    }

    if (!v.expectedAt) {
      toast("Veuillez renseigner la date prévue.");
      return;
    }
    const statusNormalized = v.status === 0 ? 1 : v.status;
    if (statusNormalized === 2) {
      if (!v.at) {
        toast("Veuillez renseigner la date du virement.");
        setOpenDetailsIndex(baseIndex);
        return;
      }
    }

    // Envoi en EUR côté back basé sur le taux contrat quand disponible
    const amountDisplayed = Number(v.amount ?? 0);
    const amountInEur = await convertDisplayedAmountToEuro(amountDisplayed, v);
    const rateToEuro = resolveRateToEuro(v);
    const rateDate = resolveRateDate(v);

    if (isFXSelected && !isVerdeoFunding && (!rateToEuro || !rateDate)) {
      toast(
        "Impossible de récupérer le taux de change à la date du contrat.",
      );
      return;
    }
    if (!Number.isFinite(amountInEur) || amountInEur < 0) {
      toast(
        "Impossible de convertir ce versement en euro avec le taux du contrat.",
      );
      return;
    }

    const amountConverted =
      rateToEuro && rateToEuro > 0
        ? isFXSelected
          ? amountDisplayed
          : amountInEur / rateToEuro
        : null;

    const payload: any = {
      realfunding: funding.id,
      amount: amountInEur,
      expectedAt: v.expectedAt,
      status: statusNormalized,
      ...(statusNormalized === 2 ? { at: v.at } : {}),
      ...(statusNormalized === 2
        ? { paymentSlip: v.paymentSlip?.id ?? null }
        : {}),
      ...(String(v.id).startsWith("tmp-") ? {} : { toEdit: v.id }),
    };

    if (amountConverted !== null && Number.isFinite(amountConverted)) {
      payload.amountConverted = amountConverted;
      payload.amount_converted = amountConverted;
    }
    if (rateToEuro) {
      payload.oldRate = rateToEuro;
      payload.old_rate = rateToEuro;
    }
    if (rateDate) {
      payload.dateOldRate = rateDate;
      payload.date_old_rate = rateDate;
    }

    if (amountConverted !== null && Number.isFinite(amountConverted)) {
      updated[baseIndex].amountConverted = amountConverted;
    }
    if (rateToEuro) {
      updated[baseIndex].oldRate = rateToEuro;
    }
    if (rateDate) {
      updated[baseIndex].dateOldRate = rateDate;
    }

    savingVersementIdRef.current = rowId;
    setSavingVersementId(rowId);

    try {
      const res = await postData({
        url: `${
          import.meta.env.VITE_IP_API
        }/api/real-funding-expected-paid/save`,
        data: payload,
      });

      if (typeof res?.id !== "undefined") updated[baseIndex].id = res.id;

      setLocalVersements(updated);
      setEditIndex(null);
      setOriginalVersement(null);
      setOpenDetailsIndex(null);
      onChange(updated);

      toast("Le versement a été enregistré.");
    } catch (err) {
      console.error(err);
      toast("Erreur lors de l’enregistrement du versement.");
    } finally {
      if (savingVersementIdRef.current === rowId) {
        savingVersementIdRef.current = null;
      }
      setSavingVersementId((current) => (current === rowId ? null : current));
    }
  };

  /** ================== DELETE ================== */
  const handleDelete = async (baseIndex: number) => {
    if (readonly) return;
    const confirmDelete = window.confirm(
      "Êtes-vous sûr de vouloir supprimer ce versement ?"
    );
    if (!confirmDelete) return;

    const updated = [...localVersements];
    const toDelete = updated[baseIndex];

    try {
      if (toDelete.id && !String(toDelete.id).startsWith("tmp-")) {
        await saveData({
          endpoint: `${
            import.meta.env.VITE_IP_API
          }/api/real-funding-expected-paid/delete/${toDelete.id}`,
          body: {},
        });
      }
      updated.splice(baseIndex, 1);
      setLocalVersements(updated);
      onChange(updated);
    } catch (err) {
      console.error("Erreur lors de la suppression:", err);
    }
  };

  const handleCancel = (baseIndex: number) => {
    if (readonly) return;
    const updated = [...localVersements];
    if (originalVersement) {
      updated[baseIndex] = originalVersement;
    } else {
      updated.splice(baseIndex, 1);
    }
    setLocalVersements(updated);
    setEditIndex(null);
    setOriginalVersement(null);
    setOpenDetailsIndex(null);
    onChange(updated);
  };

  const openMovePanel = (baseIndex: number) => {
    if (!allowMove || moveTargets.length === 0) return;
    setEditIndex(null);
    setOriginalVersement(null);
    setOpenDetailsIndex(null);
    setMoveIndex(baseIndex);
    setMoveTargetRealFundingId(String(moveTargets[0]?.id ?? ""));
  };

  const handleMovePayment = async (baseIndex: number) => {
    if (!allowMove) return;

    const versement = localVersements[baseIndex];
    if (!versement?.id || String(versement.id).startsWith("tmp-")) {
      toast("Enregistre d’abord ce versement avant de le déplacer.");
      return;
    }

    const targetRealFundingId = Number(moveTargetRealFundingId);
    if (!Number.isFinite(targetRealFundingId) || targetRealFundingId <= 0) {
      toast("Choisissez le contrat cible.");
      return;
    }

    try {
      setMovingPaymentId(versement.id);
      const res = await postData({
        url: `${
          import.meta.env.VITE_IP_API
        }/api/real-funding-expected-paid/move?by=project&onlyFunding=true`,
        data: {
          paymentId: versement.id,
          targetRealFundingId,
        },
      });

      if (
        res &&
        typeof res === "object" &&
        "success" in res &&
        (res as any).success === false
      ) {
        throw new Error(
          (res as any)?.message || "Échec du déplacement du versement.",
        );
      }

      setMoveIndex(null);
      setMoveTargetRealFundingId("");

      if (onApiList) {
        onApiList(res);
      }

      toast("Le versement a été déplacé.");
    } catch (err: any) {
      console.error(err);
      toast(err?.message || "Erreur lors du déplacement du versement.");
    } finally {
      setMovingPaymentId(null);
    }
  };

  const addVersement = async () => {
    if (readonly) return;
    if (editIndex !== null) {
      toast("Termine l’édition en cours avant d’ajouter un versement.");
      return;
    }

    const newVersement = {
      id: `tmp-${Date.now()}`,
      amount: 0,
      expectedAt: new Date().toISOString().slice(0, 10), // Date prévue par défaut
      status: 1, // Prévu
      at: "", // rempli uniquement si Reçu
      paymentSlip: { id: null, name: "", uri: "#" },
      amountConverted: null,
      oldRate: fundingRateToEuro,
      dateOldRate: fundingRateDate,
    };

    const updated = [...localVersements, newVersement];
    setLocalVersements(updated);
    setEditIndex(updated.length - 1);
    setOriginalVersement(null);
    setOpenDetailsIndex(null);
    onChange(updated);
  };

  /** ================== Composants UI ================== */

  // Switch “Reçu ?”
  const ReceivedSwitch = ({
    checked,
    onToggle,
  }: {
    checked: boolean;
    onToggle: () => void;
  }) => (
    <button
      type="button"
      onClick={() => {
        if (!readonly) onToggle();
      }}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
        checked ? "bg-green-600" : "bg-gray-300"
      } ${readonly ? "opacity-50 cursor-not-allowed" : ""}`}
      aria-pressed={checked}
      aria-label="Reçu ?"
      title={checked ? "Reçu : Oui" : "Reçu : Non"}
      disabled={readonly}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
          checked ? "translate-x-5" : "translate-x-1"
        }`}
      />
    </button>
  );

  // === FX Info (popover desktop à côté du label + cartouche mobile sous le label) ===
  const RateInfo = ({ v }: { v: any }) => {
    if (v.status !== 2 || !v.oldRate || !v.dateOldRate) return null;
    const d = new Date(v.dateOldRate);
    const dateStr = isNaN(d.getTime())
      ? v.dateOldRate
      : d.toLocaleDateString("fr-FR");
    const targetSymbol = isFXSelected
      ? currency?.symbol || currency?.code || ""
      : "€";

    return (
      <>
        {/* Desktop: icône popover à côté du label */}
        <div className="hidden sm:inline-flex">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center text-gray-500 hover:text-gray-700"
                aria-label="Voir le taux utilisé"
                title="Voir le taux utilisé"
              >
                <Info size={14} />
              </button>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="start"
              className="text-xs w-[320px] max-w-[90vw] p-3"
            >
              <div className="space-y-1">
                <div className="font-medium">Taux utilisé</div>
                <div>
                  1€ ={" "}
                  <strong>
                    {(1 / Number(v.oldRate)).toLocaleString("fr-FR")}{" "}
                    {targetSymbol}
                  </strong>
                </div>
                <div>
                  au <strong>{dateStr}</strong>
                </div>
                {v.amountConverted != null && (
                  <div className="mt-1 text-[11px] text-gray-600">
                    Montant converti:{" "}
                    <strong>
                      {Number(v.amountConverted).toLocaleString("fr-FR")}{" "}
                      {targetSymbol}
                    </strong>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Mobile: cartouche sous le label */}
        <div className="sm:hidden mt-1 text-[11px] bg-gray-100 border border-gray-200 rounded px-2 py-1 text-gray-700">
          <div className="font-medium mb-0.5">Taux utilisé</div>
          <div>
            1€ ={" "}
            <strong>
              {(1 / Number(v.oldRate)).toLocaleString("fr-FR")} {targetSymbol}
            </strong>
          </div>
          <div>
            au <strong>{dateStr}</strong>
          </div>
          {v.amountConverted != null && (
            <div className="mt-0.5">
              Montant converti:{" "}
              <strong>
                {Number(v.amountConverted).toLocaleString("fr-FR")}{" "}
                {targetSymbol}
              </strong>
            </div>
          )}
        </div>
      </>
    );
  };

  // Montant affiché : si la devise non-EUR est sélectionnée ET reçu => amountConverted si dispo.
  const displayAmount = (v: any) => {
    if (isFXSelected) {
      if (v.status === 2 && v.amountConverted != null)
        return Number(v.amountConverted);
      return Number(v.amount);
    }
    // EUR sélectionné (null) => on reste sur amount (EUR)
    return Number(v.amount);
  };

  /** ================== Render ================== */
  return (
    <div className="mt-4">
      {/* Header + bouton d’ajout */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-gray-900 font-medium flex items-center gap-2">
          <ArrowRightLeft size={18} className="" />
          Versements reçus
          {expectedPaymentsExceedContract ? (
            <div
              onMouseEnter={() => setExpectedOverflowPopoverOpen(true)}
              onMouseLeave={() => setExpectedOverflowPopoverOpen(false)}
              className="inline-flex"
            >
              <Popover open={expectedOverflowPopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex cursor-help text-orange-500"
                    aria-label={expectedPaymentsExceedMessage}
                    onFocus={() => setExpectedOverflowPopoverOpen(true)}
                    onBlur={() => setExpectedOverflowPopoverOpen(false)}
                  >
                    <AlertTriangle size={15} />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="top"
                  align="start"
                  className="w-[320px] max-w-[90vw] p-3 text-[11px] leading-relaxed text-slate-700"
                >
                  {expectedPaymentsExceedMessage}
                </PopoverContent>
              </Popover>
            </div>
          ) : null}
        </h2>
        {!readonly && !hidePostActions && (
          <button
            onClick={addVersement}
            className="text-sm text-green-600 hover:underline inline-flex items-center gap-1"
          >
            <Plus size={16} /> Ajouter un versement
          </button>
        )}
      </div>

      {displayVersements.map((v: any) => {
        // On remappe chaque ligne triée vers son index réel dans localVersements
        const baseIndex = localVersements.findIndex((x: any) => x.id === v.id);
        const isReceived = v.status === 2;
        const detailsOpen = openDetailsIndex === baseIndex;
        const amountForDisplay = displayAmount(v);
        const isSaving = savingVersementId === v.id;
        const canMovePayment =
          allowMove &&
          !hidePostActions &&
          moveTargets.length > 0 &&
          !String(v?.id ?? "").startsWith("tmp-");
        const isMoveOpen = moveIndex === baseIndex;
        const isMovingThisPayment = movingPaymentId === v.id;

        return (
          <div key={v.id} className="bg-gray-50 p-3 rounded mb-2">
            {!readonly && editIndex === baseIndex ? (
              <>
                {/* Ligne compacte (mobile-friendly) */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-start">
                  {/* Montant */}
                  <div className="relative">
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-gray-600">Montant</label>
                      {isFXSelected && <RateInfo v={v} />}
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      value={Number.isFinite(v.amount) ? v.amount : 0}
                      onChange={(e) =>
                        handleChange(baseIndex, "amount", e.target.value)
                      }
                      className="mt-1 input w-full border rounded px-2 py-1"
                      disabled={readonly}
                    />
                    <span className="absolute right-2 bottom-[-6px] -translate-y-1/2 text-sm text-gray-600">
                      {currencySymbol}
                    </span>
                  </div>

                  {/* Date prévue */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Date prévue
                    </label>
                    <input
                      type="date"
                      value={v.expectedAt || ""}
                      onChange={(e) =>
                        handleChange(baseIndex, "expectedAt", e.target.value)
                      }
                      className="input w-full border rounded px-2 py-1"
                      disabled={readonly}
                    />
                  </div>

                  {/* Reçu ? */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Reçu ?
                    </label>
                    <div className="flex items-center gap-3">
                      <ReceivedSwitch
                        checked={isReceived}
                        onToggle={() =>
                          handleChange(baseIndex, "status", isReceived ? 1 : 2)
                        }
                      />
                      {isReceived ? (
                        <span className="text-green-700 text-sm inline-flex items-center gap-1">
                          <Check size={16} /> Oui
                        </span>
                      ) : (
                        <span className="text-gray-600 text-sm inline-flex items-center gap-1">
                          <X size={16} /> Non
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-end gap-2 justify-end">
                    {!hidePostActions && (
                      <button
                        onClick={() => handleSave(baseIndex)}
                        className={`text-sm font-medium ${
                          isSaving
                            ? "cursor-not-allowed text-gray-400"
                            : "text-green-600"
                        }`}
                        title="Enregistrer"
                        disabled={readonly || isSaving}
                      >
                        {isSaving ? "Enregistrement..." : "Save"}
                      </button>
                    )}
                    <button
                      onClick={() => handleCancel(baseIndex)}
                      className="text-gray-600 text-sm font-medium"
                      title="Annuler"
                      disabled={readonly}
                    >
                      Annuler
                    </button>
                  </div>
                </div>

                {/* Panneau repliable : Détails reçu (date virement + PJ) */}
                <div className="mt-2">
                  <button
                    type="button"
                    disabled={!isReceived}
                    onClick={() =>
                      setOpenDetailsIndex(detailsOpen ? null : baseIndex)
                    }
                    className={`flex items-center gap-1 text-sm underline ${
                      isReceived
                        ? "text-blue-700"
                        : "text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Détails reçu (date du virement & pièce jointe)
                    {isReceived && (
                      <ChevronRight
                        size={16}
                        className={"ml-1 " + (detailsOpen ? "rotate-90" : "")}
                      />
                    )}
                  </button>

                  {isReceived && detailsOpen && (
                    <div className="mt-3 rounded border border-gray-200 bg-white p-3 space-y-3">
                      {/* Date du virement */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Date du virement
                        </label>
                        <input
                          type="date"
                          value={v.at || ""}
                          onChange={(e) =>
                            handleChange(baseIndex, "at", e.target.value)
                          }
                          className="input w-full border rounded px-2 py-1"
                          disabled={readonly}
                        />
                      </div>

                      {/* PJ */}
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">
                          Pièce jointe (bordereau)
                        </label>
                        {v.paymentSlip?.id ? (
                          <div className="flex items-center gap-2">
                            <a
                              href={
                                import.meta.env.VITE_MEDIA_BASE +
                                v.paymentSlip.uri
                              }
                              target="_blank"
                              className="underline text-sm"
                              rel="noreferrer"
                            >
                              📄 {v.paymentSlip.name}
                            </a>
                            {!readonly && (
                              <label className="ml-2 inline-flex items-center gap-1 text-xs cursor-pointer text-blue-700">
                                <Upload size={14} />
                                <span>Changer</span>
                                <input
                                  type="file"
                                  className="hidden"
                                  onChange={(e) => {
                                    if (e.target.files?.[0])
                                      uploadMedia(baseIndex, e.target.files[0]);
                                  }}
                                />
                              </label>
                            )}
                          </div>
                        ) : (
                          !readonly && (
                            <label className="inline-flex items-center gap-1 text-sm cursor-pointer text-blue-700">
                              <Upload size={16} />
                              <span>Ajouter une pièce jointe</span>
                              <input
                                type="file"
                                className="hidden"
                                onChange={(e) => {
                                  if (e.target.files?.[0])
                                    uploadMedia(baseIndex, e.target.files[0]);
                                }}
                              />
                            </label>
                          )
                        )}
                      </div>

                      <p className="text-xs text-gray-500">
                        Astuce : sur petit écran, renseigne Montant & Date
                        prévue, puis ouvre “Détails reçu”.
                      </p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* LECTURE / LISTING compact */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-start">
                  {/* Montant en 2 lignes: label + (popover/cartouche) puis valeur en dessous */}
                  <div>
                    <div className="flex items-center gap-1">
                      <strong>Montant</strong>
                      {isFXSelected && <RateInfo v={v} />}
                    </div>
                    <div className="mt-1">
                      {Math.trunc(amountForDisplay).toLocaleString()}{" "}
                      {currencySymbol}
                    </div>
                  </div>

                  <div>
                    <strong>Date prévue :</strong>{" "}
                    {v.expectedAt
                      ? new Date(v.expectedAt).toLocaleDateString("fr-FR")
                      : "—"}
                  </div>

                  <div className="flex items-center gap-2">
                    <strong>Reçu ?</strong>
                    {isReceived ? (
                      <Check
                        size={18}
                        className="text-green-600"
                        aria-label="Reçu"
                      />
                    ) : (
                      <X
                        size={18}
                        className="text-red-600"
                        aria-label="Non reçu"
                      />
                    )}
                  </div>

                  <div className="flex items-center gap-2 justify-end">
                    {canMovePayment && (
                      <button
                        type="button"
                        onClick={() => {
                          if (isMoveOpen) {
                            setMoveIndex(null);
                            setMoveTargetRealFundingId("");
                            return;
                          }
                          openMovePanel(baseIndex);
                        }}
                        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
                        title="Déplacer vers un autre contrat du projet"
                        disabled={isMovingThisPayment}
                      >
                        <ArrowRightLeft size={16} />
                        Déplacer
                      </button>
                    )}
                    {!readonly && !hidePostActions && (
                      <>
                        <button
                          onClick={() => {
                            setOriginalVersement({
                              ...localVersements[baseIndex],
                            });
                            setEditIndex(baseIndex);
                            setOpenDetailsIndex(null);
                          }}
                          className="text-sm"
                          title="Modifier"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(baseIndex)}
                          className="text-red-500 text-sm"
                          title="Supprimer"
                        >
                          <Trash size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {canMovePayment && isMoveOpen && (
                  <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 space-y-3">
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-slate-900">
                        Déplacer ce versement
                      </div>
                      <p className="text-xs text-slate-600">
                        Le versement sera retiré de ce contrat et rattaché au
                        contrat cible du même projet.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-xs text-slate-600">
                        Contrat cible
                      </label>
                      <select
                        value={moveTargetRealFundingId}
                        onChange={(e) =>
                          setMoveTargetRealFundingId(e.target.value)
                        }
                        className="w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                        disabled={isMovingThisPayment}
                      >
                        <option value="">Choisir un contrat</option>
                        {moveTargets.map((target) => (
                          <option key={target.id} value={target.id}>
                            {target.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setMoveIndex(null);
                          setMoveTargetRealFundingId("");
                        }}
                        className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700"
                        disabled={isMovingThisPayment}
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMovePayment(baseIndex)}
                        className="rounded bg-slate-900 px-3 py-1.5 text-sm text-white disabled:opacity-60"
                        disabled={
                          isMovingThisPayment || !moveTargetRealFundingId
                        }
                      >
                        {isMovingThisPayment ? "Déplacement..." : "Confirmer"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Panneau repliable : Détails reçu (read-only) */}
                <div className="mt-2">
                  <button
                    type="button"
                    disabled={!isReceived}
                    onClick={() =>
                      setOpenDetailsIndex(detailsOpen ? null : baseIndex)
                    }
                    className={`flex items-center gap-1 text-sm underline ${
                      isReceived
                        ? "text-blue-700"
                        : "text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Détails reçu (date du virement & pièce jointe)
                    {isReceived && (
                      <ChevronRight
                        size={16}
                        className={"ml-1 " + (detailsOpen ? "rotate-90" : "")}
                      />
                    )}
                  </button>

                  {isReceived && detailsOpen && (
                    <div className="mt-3 rounded border border-gray-200 bg-white p-3 space-y-2">
                      <div>
                        <strong>Date du virement :</strong>{" "}
                        {v.at
                          ? new Date(v.at).toLocaleDateString("fr-FR")
                          : "—"}
                      </div>
                      <div>
                        <strong>PJ :</strong>{" "}
                        {v.paymentSlip?.id ? (
                          <a
                            href={
                              import.meta.env.VITE_MEDIA_BASE +
                              v.paymentSlip.uri
                            }
                            target="_blank"
                            className="underline"
                            rel="noreferrer"
                          >
                            {v.paymentSlip.name}
                          </a>
                        ) : (
                          <span className="text-gray-500">—</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}

      {displayVersements.length === 0 && (
        <p className="text-gray-600 italic mt-4">Aucun versement enregistré.</p>
      )}
    </div>
  );
};

export default VersementCard;
